/**
 * Work Order — canonical "upcoming mission" artifact owned by an
 * applicator organization.
 *
 * Mirrors `adn_work_orders` on the Hub. Two origin paths:
 * - **Bid-derived** (`jobId` set): auto-created when the org wins a
 *   CropSwipe bid. UI shows a "From CropSwipe" source chip.
 * - **Manual** (`jobId` null): created by an org Owner / Chief
 *   Supervisor via the new-WO form on this web module. UI shows a
 *   "Manual" source chip.
 *
 * Status vocabulary (Hub wire → UI label):
 *   `open` → "Scheduled"
 *   `in_progress` → "In Progress"
 *   `completed` → "Complete"
 *   `cancelled` → "Cancelled"
 *
 * See: agent-os/specs/2026-04-30-marketplace-work-orders/spec.md
 */
export type WorkOrderStatus = 'open' | 'in_progress' | 'completed' | 'cancelled';

export interface WorkOrder {
  id: string;
  organizationId: string;
  assignedPilotProfileId?: string | null;
  jobId?: string | null;
  customerId?: string | null;
  fieldId?: string | null;

  title: string;
  description?: string | null;
  status: WorkOrderStatus;

  scheduledDate?: string | null;
  scheduledTime?: string | null;

  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;

  // Acreage hints — surfaced when the Hub denormalizes them onto the
  // list/detail row. `actualAcres` flows from the linked job for
  // marketplace WOs; `estimatedAcres` is the field's acreage. The
  // mobile Mark Complete dialog seeds itself from these.
  actualAcres?: number | null;
  estimatedAcres?: number | null;

  // Spray-record linkage (populated once the pilot logs a spray record
  // from this WO).
  sprayRecordId?: string | null;

  // Enriched display fields the Hub joins in via
  // `WorkOrderService.enrichMany`. The detail screen reads these
  // directly so customer / field / location don't appear blank when
  // local caches are cold.
  fieldName?: string | null;
  fieldAcreage?: number | null;
  cropType?: string | null;
  fieldCity?: string | null;
  fieldState?: string | null;
  fieldLatitude?: number | null;
  fieldLongitude?: number | null;
  customerName?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  assignedPilotName?: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkOrderDto {
  title: string;
  description?: string;
  customerId?: string | null;
  fieldId?: string | null;
  assignedPilotProfileId?: string | null;
  scheduledDate?: string | null;
  scheduledTime?: string | null;
}

export interface UpdateWorkOrderDto extends Partial<CreateWorkOrderDto> {}

export interface CancelWorkOrderDto {
  reason: string;
}

/** Pilot-friendly label per the 2026-05-15 design decision. */
export function workOrderStatusLabel(status: WorkOrderStatus): string {
  switch (status) {
    case 'open':
      return 'Scheduled';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Complete';
    case 'cancelled':
      return 'Cancelled';
  }
}

/**
 * Bootstrap badge class for a status pill. Picked to match the rest
 * of the app's badge conventions (Spray Records uses the same family).
 */
export function workOrderStatusBadgeClass(status: WorkOrderStatus): string {
  switch (status) {
    case 'open':
      return 'badge bg-primary-subtle text-primary';
    case 'in_progress':
      return 'badge bg-warning-subtle text-warning';
    case 'completed':
      return 'badge bg-success-subtle text-success';
    case 'cancelled':
      return 'badge bg-danger-subtle text-danger';
  }
}

/**
 * "From CropSwipe" if the WO is linked to a marketplace job, else
 * "Manual". Used by the list + detail screens.
 */
export function workOrderSourceLabel(wo: WorkOrder): 'From CropSwipe' | 'Manual' {
  return wo.jobId ? 'From CropSwipe' : 'Manual';
}

/**
 * Human-readable location for a WO. Prefers field city/state and
 * falls back to the customer's address when the field carries none.
 * Returns an empty string when nothing is known so the caller can
 * decide on its own placeholder.
 */
export function workOrderLocationDisplay(wo: WorkOrder): string {
  const fieldLoc = [wo.fieldCity, wo.fieldState]
    .filter((s) => s && s.trim())
    .join(', ');
  if (fieldLoc) return fieldLoc;
  const customerLoc = [wo.customerCity, wo.customerState]
    .filter((s) => s && s.trim())
    .join(', ');
  return customerLoc;
}

/** Acreage shown on summary cards — prefers actual, falls back to estimated. */
export function workOrderAcresDisplay(wo: WorkOrder): number | null {
  return wo.actualAcres ?? wo.estimatedAcres ?? null;
}
